import React, { useState, useEffect } from "react";
import api from "../../services/api";
import { CONTRACTS } from "./salary/constants";
import { PERIOD } from "./../../constants";
import { getCostSalary, getCostExpenses, getMonthlyTotalCost } from "./salary/salary";
import { calculateMargin } from "../../utils";
import { HiCurrencyEuro } from "react-icons/hi2";

export default function Payment({ user, setUser }) {
  const [values, setValues] = useState(user);
  const [organisations, setOrganisations] = useState([]);

  useEffect(() => {
    api.get("/organisation").then((res) => {
      if (res.ok) setOrganisations(res.data);
    });
  }, []);

  useEffect(() => {
    if (!values.days_worked && values.contract !== "FREELANCE") return;
    if (!values.brut_salary && values.contract !== "FREELANCE") return;
    
    let total_monthly_cost;
    let tjm_salary;
    let tjm_expense; 
    let payment_type = values.payment_type;

    if (values.payment_type === "DAILY" && values.contract === "FREELANCE") {
      tjm_salary = values.payment_value;
      tjm_expense = 0;
    } else if (values.payment_type === "MONTHLY" && values.contract === "FREELANCE") {
      tjm_salary = (values.payment_value / values.days_worked).toFixed(0);
      tjm_expense = 0;
      total_monthly_cost = values.payment_value;
    } else {
      tjm_salary = getCostSalary(values);
      tjm_expense = getCostExpenses(values);
      total_monthly_cost = getMonthlyTotalCost(values);
      if (values.contract !== "FREELANCE") payment_type = "MONTHLY";
    }

    const tjm = parseFloat(values.tjm_salary || 0) + parseFloat(values.tjm_expense || 0);

    if (values.tjm !== tjm || values.tjm_salary !== tjm_salary || values.tjm_expense !== tjm_expense || values.total_monthly_cost !== total_monthly_cost) {
      setValues({ ...values, tjm, tjm_salary, tjm_expense, total_monthly_cost, payment_type });
    }
  }, [values]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="w-full">
      <div className="mb-4">
        <label className="font-bold text-lg">Payment Information</label>
        <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm px-3 py-2">
          Lecture seule — les conditions de paie sont gérées par périodes dans l'onglet <span className="font-semibold">Contracts</span> (une augmentation = une nouvelle période). Cet écran reflète la période active.
        </div>
      </div>

      <fieldset disabled className="block w-full p-6 border border-gray-200 rounded-lg shadow-sm bg-white">
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-sm text-gray-700 font-medium mb-1">Contract Type</div>
              <select
                className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors duration-200 text-sm p-2"
                name="contract"
                value={values.contract}
                onChange={handleChange}>
                <option value="">Select Contract Type</option>
                {Object.keys(CONTRACTS).map(e => (
                  <option key={e} value={e}>{e}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="text-sm text-gray-700 font-medium mb-1">Organisation</div>
              <select
                className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors duration-200 text-sm p-2"
                name="organisation_name"
                value={values.organisation_name || ""}
                onChange={handleChange}>
                <option value="">Select Organisation</option>
                {organisations
                  .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                  .map((org) => (
                    <option key={org._id} value={org.name}>{org.name}</option>
                  ))}
              </select>
            </div>
          </div>

          <PaymentFreelance values={values} handleChange={handleChange}/>
          <PaymentEmployee values={values} handleChange={handleChange}/>
        </div>
      </fieldset>
    </div>
  );
}

const PaymentEmployee = ({ values, handleChange }) => {
  if (values.contract === CONTRACTS.FREELANCE) return null;

  const margin = calculateMargin(values.tjm, values.tjms);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <div className="text-sm text-gray-700 font-medium mb-1">Annual Gross Salary</div>
          <input
            className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors duration-200 text-sm p-2"
            name="brut_salary"
            type="number"
            placeholder="Enter annual salary"
            value={values.brut_salary}
            onChange={handleChange}
          />
        </div>
        <div>
          <div className="text-sm text-gray-700 font-medium mb-1">Working Days</div>
          <input
            className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors duration-200 text-sm p-2"
            name="days_worked"
            type="number"
            placeholder="Number of days"
            value={values.days_worked}
            onChange={handleChange}
          />
        </div>
        {values.contract === CONTRACTS.APPRENTICESHIP && (
          <div>
            <div className="text-sm text-gray-700 font-medium mb-1">Annual School Cost</div>
            <input
              className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors duration-200 text-sm p-2"
              name="school_cost"
              type="number"
              placeholder="School fees"
              value={values.school_cost}
              onChange={handleChange}
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <div className="text-sm text-gray-700 font-medium mb-1">Monthly Total Cost</div>
          <input
            className="w-full rounded-md border border-gray-300 bg-gray-50 text-sm p-2"
            name="total_monthly_cost"
            type="number"
            value={values.total_monthly_cost}
            disabled
          />
        </div>
        <div>
          <div className="text-sm text-gray-700 font-medium mb-1">Monthly Net Payment</div>
          <input
            className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors duration-200 text-sm p-2"
            name="payment_value"
            type="number"
            placeholder="Net payment"
            value={values.payment_value}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <div className="text-sm text-gray-700 font-medium mb-1">Daily Salary Cost</div>
          <input
            className="w-full rounded-md border border-gray-300 bg-gray-50 text-sm p-2"
            name="tjm_salary"
            type="number"
            value={values.tjm_salary}
            disabled
          />
        </div>
        <div>
          <div className="text-sm text-gray-700 font-medium mb-1">Daily Expense Cost</div>
          <input
            className="w-full rounded-md border border-gray-300 bg-gray-50 text-sm p-2"
            name="tjm_expense"
            type="number"
            value={values.tjm_expense}
            disabled
          />
        </div>
        <div>
          <div className="text-sm text-gray-700 font-medium mb-1">Total Daily Cost</div>
          <input
            className="w-full rounded-md border border-gray-300 bg-gray-50 text-sm p-2"
            name="tjm"
            type="number"
            value={values.tjm}
            disabled
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="text-sm text-gray-700 font-medium mb-1">Daily Sales Rate (TJMS)</div>
          <input
            className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors duration-200 text-sm p-2"
            name="tjms"
            type="number"
            placeholder="Enter TJMS"
            value={values.tjms}
            onChange={handleChange}
          />
        </div>
        <div>
          <div className="text-sm text-gray-700 font-medium mb-1 flex items-center gap-2">
            Margin <HiCurrencyEuro className="text-gray-500" />
          </div>
          <input
            className={`w-full rounded-md border border-gray-300 bg-gray-50 text-sm p-2 font-medium ${
              margin < 15 ? "text-red-600" : "text-green-600"
            }`}
            value={`${margin}%`}
            disabled
          />
        </div>
      </div>
    </div>
  );
};

const PaymentFreelance = ({ values, handleChange }) => {
  if (values.contract !== CONTRACTS.FREELANCE) return null;

  const margin = calculateMargin(values.tjm, values.tjms);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <div className="text-sm text-gray-700 font-medium mb-1">Payment Type</div>
          <select
            className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors duration-200 text-sm p-2"
            name="payment_type"
            value={values.payment_type}
            onChange={handleChange}>
            <option value="">Select Payment Type</option>
            {Object.keys(PERIOD).map(e => (
              <option key={e} value={e}>{e}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="text-sm text-gray-700 font-medium mb-1">Payment Amount</div>
          <input
            className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors duration-200 text-sm p-2"
            name="payment_value"
            type="number"
            placeholder="Enter amount"
            value={values.payment_value}
            onChange={handleChange}
          />
        </div>
        {values.payment_type === "MONTHLY" && (
          <div>
            <div className="text-sm text-gray-700 font-medium mb-1">Monthly Cost</div>
            <input
              className="w-full rounded-md border border-gray-300 bg-gray-50 text-sm p-2"
              name="total_monthly_cost"
              type="number"
              value={values.total_monthly_cost}
              disabled
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <div className="text-sm text-gray-700 font-medium mb-1">Daily Rate (TJM)</div>
          <input
            className="w-full rounded-md border border-gray-300 bg-gray-50 text-sm p-2"
            name="tjm"
            value={values.tjm}
            disabled
          />
        </div>
        <div>
          <div className="text-sm text-gray-700 font-medium mb-1">Daily Expense Rate</div>
          <input
            className="w-full rounded-md border border-gray-300 bg-gray-50 text-sm p-2"
            name="tjm_expense"
            value={values.tjm_expense}
            disabled
          />
        </div>
        <div>
          <div className="text-sm text-gray-700 font-medium mb-1">Sales Rate</div>
          <input
            className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors duration-200 text-sm p-2"
            name="tjms"
            type="number"
            placeholder="Enter sales rate"
            value={values.tjms}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="text-sm text-gray-700 font-medium mb-1 flex items-center gap-2">
            Margin <HiCurrencyEuro className="text-gray-500" />
          </div>
          <input
            className={`w-full rounded-md border border-gray-300 bg-gray-50 text-sm p-2 font-medium ${
              margin < 15 ? "text-red-600" : "text-green-600"
            }`}
            value={`${margin}%`}
            disabled
          />
        </div>
      </div>
    </div>
  );
};

